package com.example.mystybag.appCore.views.pages

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import com.example.mystybag.appCore.views.components.MenuPhotosSection
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.pages.ReservationDetailsBottomSheetViewModel
import com.example.mystybag.appCore.model.pages.ReservationDetailsBottomSheetUIState
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.DateTimeHelper
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.DefaultButtonType
import com.example.mystybag.sharedmodels.project.database.UserReservationModel
import com.example.mystybag.sharedmodels.project.logic.enums.ReservationStatus
import kotlinx.datetime.Clock
import dev.icerock.moko.resources.compose.stringResource

@Composable
private fun getReviewCategoryLabel(labelKey: String): String {
    return try {
        when (labelKey) {
            "collection_experience" -> sharedString(SR.strings.collection_experience)
            "food_quality" -> sharedString(SR.strings.food_quality)
            "variety_of_contents" -> sharedString(SR.strings.variety_of_contents)
            "food_quantity" -> sharedString(SR.strings.food_quantity)
            "review_rating_freshness" -> sharedString(SR.strings.review_rating_freshness)
            "review_rating_quality" -> sharedString(SR.strings.review_rating_quality)
            "review_rating_variety" -> sharedString(SR.strings.review_rating_variety)
            "review_rating_value" -> sharedString(SR.strings.review_rating_value)
            "review_rating_taste" -> sharedString(SR.strings.review_rating_taste)
            "review_rating_presentation" -> sharedString(SR.strings.review_rating_presentation)
            "review_rating_portion_size" -> sharedString(SR.strings.review_rating_portion_size)
            "review_rating_coffee_quality" -> sharedString(SR.strings.review_rating_coffee_quality)
            "review_rating_service" -> sharedString(SR.strings.review_rating_service)
            "review_rating_atmosphere" -> sharedString(SR.strings.review_rating_atmosphere)
            "review_rating_product_quality" -> sharedString(SR.strings.review_rating_product_quality)
            "review_rating_packaging" -> sharedString(SR.strings.review_rating_packaging)
            "review_rating_service_quality" -> sharedString(SR.strings.review_rating_service_quality)
            "review_rating_professionalism" -> sharedString(SR.strings.review_rating_professionalism)
            "review_rating_hygiene" -> sharedString(SR.strings.review_rating_hygiene)
            "review_rating_service_speed" -> sharedString(SR.strings.review_rating_service_speed)
            "review_rating_price" -> sharedString(SR.strings.review_rating_price)
            "review_rating_accessibility" -> sharedString(SR.strings.review_rating_accessibility)
            "review_rating_effectiveness" -> sharedString(SR.strings.review_rating_effectiveness)
            "review_rating_service_consultation" -> sharedString(SR.strings.review_rating_service_consultation)
            "review_rating_packaging_transport" -> sharedString(SR.strings.review_rating_packaging_transport)
            else -> labelKey // Fallback to key if not found
        }
    } catch (e: Exception) {
        labelKey // Fallback to key if error
    }
}

@Composable
fun ReservationDetailsBottomSheetView(viewModel: ReservationDetailsBottomSheetViewModel) {
    val uiState by viewModel.uiState.subscribeAsState()

    Box(
        modifier = Modifier
            .fillMaxWidth(),
        contentAlignment = Alignment.TopCenter
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(p16),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(p16)
        ) {
        if (uiState.isError) {
            Texts.TitleLarge(
                text = sharedString(SR.strings.error),
                textAlign = TextAlign.Center,
                color = AppTheme.current.colors.error
            )
            Texts.BodyMedium(
                text = uiState.errorMessage,
                textAlign = TextAlign.Center
            )
            Button(
                onClick = { viewModel.onCloseClick() },
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = p32),
                colors = ButtonDefaults.buttonColors(
                    containerColor = AppTheme.current.colors.primary
                ),
                shape = RoundedCornerShape(p12)
            ) {
                Texts.LabelLarge(
                    text = sharedString(SR.strings.close),
                    color = AppTheme.current.colors.onPrimary
                )
            }
        } else {
            // Reservation details
            Column(
                modifier = Modifier.fillMaxWidth(),
                horizontalAlignment = Alignment.Start,
                verticalArrangement = Arrangement.spacedBy(p12)
            ) {
                Texts.TitleLarge(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = p12, bottom = p12),
                    text = sharedString(SR.strings.reservation_details),
                    textAlign = TextAlign.Center
                )
                HorizontalDivider(color = AppTheme.current.colors.outlineVariant)
                Spacer(modifier = Modifier.height(p32))

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.status) + ":",
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                    Texts.TitleSmall(
                        text = getStatusText(uiState.reservation),
                        color = getStatusColor(uiState.reservation)
                    )
                }

                // Сума (от детайлите на торбата – priceWithDiscount * bagsCount)
                uiState.loadedAmountFormatted?.let { amountFormatted ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Texts.BodyMedium(
                            text = sharedString(SR.strings.amount) + ":",
                            color = AppTheme.current.colors.onSurfaceVariant
                        )
                        Texts.TitleSmall(
                            text = amountFormatted,
                            color = AppTheme.current.colors.onSurface
                        )
                    }
                }

                // dateTimeReceived – показваме само ако не е null
                uiState.reservation.dateTimeReceived?.let { receivedTime ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Texts.BodyMedium(
                            text = sharedString(SR.strings.received_on) + ":",
                            color = AppTheme.current.colors.onSurfaceVariant
                        )
                        Texts.TitleSmall(
                            text = DateTimeHelper.parseDateFromMilliseconds(receivedTime),
                            color = AppTheme.current.colors.onSurface
                        )
                    }
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.bag_id) + ":",
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                    Texts.TitleSmall(
                        text = "${uiState.reservation.bagId}",
                        color = AppTheme.current.colors.onSurface
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.bags_count) + ":",
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                    Texts.TitleSmall(
                        text = "${uiState.reservation.bagsCount}",
                        color = AppTheme.current.colors.onSurface
                    )
                }

                // Адрес за взимане – от локацията (loadedPickupAddress); при натискане отваря картата
                val pickupAddress = uiState.loadedPickupAddress?.takeIf { it.isNotBlank() }
                val hasPickupAddress = pickupAddress != null
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .then(
                            if (hasPickupAddress) Modifier.clickable { viewModel.onPickupAddressClick() }
                            else Modifier
                        ),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.pickup_address) + ":",
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                    AutoResizeSingleLineTitleSmall(
                        text = pickupAddress ?: "—",
                        modifier = Modifier.weight(1f),
                        color = if (hasPickupAddress) AppTheme.current.colors.onPrimaryContainer else AppTheme.current.colors.onSurfaceVariant,
                        textDecoration = if (hasPickupAddress) TextDecoration.Underline else null,
                        textAlign = TextAlign.End
                    )
                }

                // От кога до кога могат да си получат поръчката – от детайлите на торба (loadedCollectWindowLabel)
                val collectWindowLabel = uiState.loadedCollectWindowLabel?.takeIf { it.isNotBlank() }
                Row (
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.collection_time_window) + ":",
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                    Texts.TitleSmall(
                        text = collectWindowLabel ?: "—",
                        color = AppTheme.current.colors.onSurface
                    )
                }

                uiState.matchedCatalogBag?.let {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.End
                    ) {
                        Texts.BodyMedium(
                            text = sharedString(SR.strings.view_bag),
                            color = AppTheme.current.colors.primary,
                            textDecoration = TextDecoration.Underline,
                            modifier = Modifier.clickable { viewModel.onOpenBagClick() }
                        )
                    }
                }
            }

            if (uiState.reservation.menuPhotoUrls.isNotEmpty()) {
                MenuPhotosSection(
                    menuPhotoUrls = uiState.reservation.menuPhotoUrls,
                    onMenuPhotoClick = viewModel::onMenuPhotoClick,
                    modifier = Modifier.fillMaxWidth()
                )
            }
            
            // Rating form
            if (uiState.showRatingForm) {
                RatingFormSection(
                    viewModel = viewModel,
                    uiState = uiState
                )
                Spacer(modifier = Modifier.height(p16))
            }
            
            // Success message after review submission
            if (uiState.isReviewSubmitted) {
                Texts.TitleMedium(
                    text = sharedString(SR.strings.review_submitted_successfully),
                    textAlign = TextAlign.Center,
                    color = AppTheme.current.colors.primary
                )
                Spacer(modifier = Modifier.height(p16))
            }
            
            // Action button based on status
            when {
                uiState.showRatingForm -> {
                    // Show submit button for rating form
                    Button(
                        onClick = { viewModel.onSubmitReviewClick() },
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(bottom = p32),
                        enabled = !uiState.isSubmittingReview && 
                                 uiState.ratingCategory1 > 0 &&
                                 uiState.ratingCategory2 > 0 &&
                                 uiState.ratingCategory3 > 0 &&
                                 uiState.ratingCategory4 > 0,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AppTheme.current.colors.primary
                        ),
                        shape = RoundedCornerShape(p12)
                    ) {
                        if (uiState.isSubmittingReview) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(p16),
                                color = AppTheme.current.colors.onPrimary
                            )
                        } else {
                            Texts.LabelLarge(
                                text = sharedString(SR.strings.submit_review),
                                color = AppTheme.current.colors.onPrimary
                            )
                        }
                    }
                }
                else -> {

                    DefaultButton(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(bottom = p32),
                        type = DefaultButtonType.PRIMARY,
                        caption = stringResource(SR.strings.close),
                        callback = viewModel::onCloseClick
                    )
            }
        }
        }
    }
    }
}

@Composable
private fun AutoResizeSingleLineTitleSmall(
    text: String,
    modifier: Modifier = Modifier,
    color: Color,
    textAlign: TextAlign = TextAlign.End,
    textDecoration: TextDecoration? = null
) {
    val baseStyle = AppTheme.current.typography.titleSmall
    val minFontSize = 10.sp
    var currentFontSize by remember(text) { mutableStateOf(baseStyle.fontSize) }

    Text(
        text = text,
        modifier = modifier,
        maxLines = 1,
        softWrap = false,
        overflow = TextOverflow.Ellipsis,
        textAlign = textAlign,
        textDecoration = textDecoration,
        style = baseStyle.copy(color = color, fontSize = currentFontSize),
        onTextLayout = { layoutResult ->
            if (layoutResult.hasVisualOverflow && currentFontSize > minFontSize) {
                currentFontSize = (currentFontSize.value - 0.5f).sp
            }
        }
    )
}

private fun isPickupExpired(reservation: UserReservationModel): Boolean {
    if (reservation.dateTimePickUpTo <= 0L) return false
    return Clock.System.now().toEpochMilliseconds() > reservation.dateTimePickUpTo
}

@Composable
private fun getStatusText(reservation: UserReservationModel): String {
    val status = ReservationStatus.fromInt(reservation.status)
    val isExpired = isPickupExpired(reservation)
    var raw = when {
        status == ReservationStatus.RESERVED -> sharedString(SR.strings.reserved)
        status == ReservationStatus.PAID -> sharedString(SR.strings.paid)
        status == ReservationStatus.DELIVERED_TO_CLIENT -> sharedString(SR.strings.received)
        status == ReservationStatus.PAID_TO_MERCHANT -> sharedString(SR.strings.received)
        status == ReservationStatus.CANCELED -> sharedString(SR.strings.cancel)
        else -> sharedString(SR.strings.reserved)
    }

    if (isExpired && status == ReservationStatus.PAID) {
        raw = sharedString(SR.strings.not_accepted)
    }

    return raw
}

@Composable
private fun getStatusColor(reservation: UserReservationModel): androidx.compose.ui.graphics.Color {
    val status = ReservationStatus.fromInt(reservation.status)
    val isExpired = isPickupExpired(reservation)
    val colors = AppTheme.current.colors
    var stsColor =  when {
        status == ReservationStatus.RESERVED -> colors.onSecondaryContainer
        status == ReservationStatus.PAID -> Color(0xFF1565C0)
        status == ReservationStatus.DELIVERED_TO_CLIENT -> colors.onPrimaryContainer
        status == ReservationStatus.PAID_TO_MERCHANT -> colors.onPrimaryContainer
        status == ReservationStatus.CANCELED -> colors.onSecondaryContainer
        else -> colors.onSecondaryContainer
    }

    if (isExpired && status == ReservationStatus.PAID) {
        stsColor = colors.error
    }

    return stsColor
}

@Composable
private fun RatingFormSection(
    viewModel: ReservationDetailsBottomSheetViewModel,
    uiState: ReservationDetailsBottomSheetUIState
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p16)
    ) {
        Texts.TitleMedium(
            text = sharedString(SR.strings.rate_your_experience),
            textAlign = TextAlign.Start
        )
        Texts.BodyMedium(
            text = sharedString(SR.strings.rate_your_experience_description),
            textAlign = TextAlign.Start,
            color = AppTheme.current.colors.onSurfaceVariant
        )
        
        Spacer(modifier = Modifier.height(p8))
        
        // Dynamic rating categories based on business category
        val categories = uiState.reviewCategories
        val ratings = listOf(
            uiState.ratingCategory1,
            uiState.ratingCategory2,
            uiState.ratingCategory3,
            uiState.ratingCategory4
        )
        
        if (categories.isNotEmpty() && categories.size == 4) {
            categories.forEachIndexed { index, category ->
                RatingItem(
                    label = getReviewCategoryLabel(category.labelKey),
                    rating = ratings.getOrNull(index) ?: 0,
                    onRatingChanged = { rating ->
                        viewModel.onRatingChanged(category.key, rating)
                    }
                )
                if (index < categories.size - 1) {
                    Spacer(modifier = Modifier.height(p12))
                }
            }
        } else {
            // Fallback to default categories if not configured
            RatingItem(
                label = sharedString(SR.strings.collection_experience),
                rating = uiState.ratingCategory1,
                onRatingChanged = { viewModel.onRatingChanged("collection_experience", it) }
            )
            Spacer(modifier = Modifier.height(p12))
            RatingItem(
                label = sharedString(SR.strings.food_quality),
                rating = uiState.ratingCategory2,
                onRatingChanged = { viewModel.onRatingChanged("food_quality", it) }
            )
            Spacer(modifier = Modifier.height(p12))
            RatingItem(
                label = sharedString(SR.strings.variety_of_contents),
                rating = uiState.ratingCategory3,
                onRatingChanged = { viewModel.onRatingChanged("variety_of_contents", it) }
            )
            Spacer(modifier = Modifier.height(p12))
            RatingItem(
                label = sharedString(SR.strings.food_quantity),
                rating = uiState.ratingCategory4,
                onRatingChanged = { viewModel.onRatingChanged("food_quantity", it) }
            )
        }
    }
}

@Composable
private fun RatingItem(
    label: String,
    rating: Int,
    onRatingChanged: (Int) -> Unit
) {
    Column(
        modifier = Modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(p8)
    ) {
        Texts.LabelMedium(text = label)
        Row(
            horizontalArrangement = Arrangement.spacedBy(p8)
        ) {
            repeat(5) { index ->
                val starValue = index + 1
                val isSelected = starValue <= rating
                Texts.TitleLarge(
                    text = if (isSelected) "★" else "☆",
                    color = if (isSelected) AppTheme.current.colors.primary else AppTheme.current.colors.onSurfaceVariant,
                    modifier = Modifier.clickable { onRatingChanged(starValue) }
                )
            }
        }
    }
}

